import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo, IItemForGetShortDishInfo } from 'types';
import { Checkbox } from 'antd';
import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import { AdditionalDishCategorySelect } from './AdditionalDishCategorySelect';
import { AdditionalDishSubCategorySelect } from './AdditionalDishSubCategorySelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './MultipleSelect.module.scss';

interface IProps {
	refetch: () => void;
	setFetchedAdditionalDishSubCategories: (value: IItemForGetShortInfo[]) => void;
	setAdditionalDishCategory: (value: Nullable<string>) => void;
	setAdditionalDishSubCategory: (value: Nullable<string>) => void;
	setAdditionalDishes: (value: string[]) => void;
	removeAdditionalDish: (value: string) => void;
	data: IItemForGetShortDishInfo[];
	IDsSelectedRestaurant: string[];
	selectedAdditionalDishCategory: Nullable<string>;
	selectedAdditionalDishSubCategory: Nullable<string>;
	fetchedAdditionalDishes: IItemForGetShortDishInfo[];
	selectedAdditionalDishes: string[];
	isLoading: boolean;
	responseError: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({
		refetch,
		setFetchedAdditionalDishSubCategories,
		setAdditionalDishCategory,
		setAdditionalDishSubCategory,
		setAdditionalDishes,
		removeAdditionalDish,
		data,
		IDsSelectedRestaurant,
		selectedAdditionalDishCategory,
		selectedAdditionalDishSubCategory,
		fetchedAdditionalDishes,
		selectedAdditionalDishes,
		isLoading,
		responseError,
	}) => {
		const [searchString, setSearchString] = useState<string>('');

		const filteredDataWithAdditionalContent = useMemo(() => {
			const updatedData = data.map((el) => ({
				...el,
				additionalContent: (
					<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
				),
			}));

			if (searchString) {
				const filteredDataByName = updatedData.filter((el) =>
					el.name.toLowerCase().includes(searchString.toLowerCase()),
				);
				const filteredDataByExternalId = updatedData.filter((el) =>
					el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
				);
				const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
				return unionFilteredData;
			}
			return updatedData;
		}, [searchString, data]);

		const handleSelectAllData = useCallback(() => {
			const isCheckedAllItems = data.every((el) => selectedAdditionalDishes.includes(el.id));
			const dataIDs = data.map((el) => el.id);
			if (isCheckedAllItems) {
				setAdditionalDishes(selectedAdditionalDishes.filter((el) => !dataIDs.includes(el)));
			} else {
				const filteredData = data.map((el) => el.id).filter((el) => !selectedAdditionalDishes.includes(el));
				setAdditionalDishes([...selectedAdditionalDishes, ...filteredData]);
			}
		}, [data, selectedAdditionalDishes, setAdditionalDishes]);

		useEffect(() => {
			setSearchString('');
		}, [selectedAdditionalDishSubCategory]);

		const selectedItemsList = useMemo(() => {
			return selectedAdditionalDishes.map((el) => {
				const label = fetchedAdditionalDishes?.find((item) => item.id === el)?.name || '';

				return <SelectedItem key={el} label={label} onDelete={() => removeAdditionalDish(el)} />;
			});
		}, [selectedAdditionalDishes, fetchedAdditionalDishes, removeAdditionalDish]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				const isChecked = () => {
					return data.every((el) => selectedAdditionalDishes.includes(el.id));
				};

				return (
					<div className={style.container}>
						<Text
							translationNameSpace="form"
							translationKey="fillInRequiredFilters"
							size="s"
							weight="bold"
						/>
						<div className={style.selectMenuHeader}>
							<div className={style.filterSelects}>
								<AdditionalDishCategorySelect
									IDsSelectedRestaurant={IDsSelectedRestaurant}
									setAdditionalDishCategory={setAdditionalDishCategory}
								/>
								<AdditionalDishSubCategorySelect
									selectedAdditionalDishCategory={selectedAdditionalDishCategory}
									setFetchedAdditionalDishSubCategories={setFetchedAdditionalDishSubCategories}
									setAdditionalDishSubCategory={setAdditionalDishSubCategory}
								/>
							</div>
							<SearchField onChangeSelectSearchString={setSearchString} searchString={searchString} />
							<Checkbox
								className={style.checkBox}
								checked={!!(data.length && isChecked())}
								disabled={!data.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllDishesLabel"
									asFragment={true}
								/>
							</Checkbox>
							{!!selectedItemsList.length && (
								<div className={style.selectedItemsList}>{selectedItemsList}</div>
							)}
						</div>
						{menu}
					</div>
				);
			},
			[selectedAdditionalDishes, data, handleSelectAllData, setSearchString, searchString],
		);

		return (
			<FormSelect
				name="selectedAdditionalDishes"
				label="dishesLabel"
				options={filteredDataWithAdditionalContent}
				fetchError={responseError}
				loading={isLoading}
				disabled={!IDsSelectedRestaurant.length}
				onRefetch={refetch}
				onChange={setAdditionalDishes}
				counterValue={selectedAdditionalDishes.length}
				dropdownRender={renderSelectMenu}
				mode="multiple"
			/>
		);
	},
);
